/**
 * Billing read-only gate plumbing (hueco D1). Mirrors the framework-agnostic
 * `appCompatibility` interceptor: the HTTP layer cannot touch React state, so it
 * derives a signal and dispatches it as a DOM event for a React subscriber.
 *
 * The backend returns 403 with a stable machine `code` on any write when the org
 * has no active/trial subscription. We match on the code only — never the
 * localized `errors.detail`. A gate signal does NOT itself hold "read-only"
 * state; it tells the app to refresh the onboarding query, whose
 * `steps.subscription_active` is the single source of truth for the paywall.
 */
import type { ApiError } from '@/api/ApiError';

export const billingGateEvent = 'fuelops:billing-gate';

const SUBSCRIPTION_INACTIVE_CODE = 'subscription_inactive';

/** True when an error is the backend's "writes are gated" 403 (D1). */
export const isBillingGateError = (error: ApiError): boolean =>
  error.status === 403 && error.code === SUBSCRIPTION_INACTIVE_CODE;

/** Subscribes to billing-gate signals. Returns an unsubscribe function. */
export const subscribeToBillingGate = (handler: () => void): (() => void) => {
  window.addEventListener(billingGateEvent, handler);
  return () => window.removeEventListener(billingGateEvent, handler);
};

/**
 * Interceptor entry point called by `httpClient` for every failed request.
 * Dispatches a gate signal when the backend reports the org can no longer write,
 * so the proactive onboarding state can refresh and the paywall surfaces.
 */
export const reportBillingGateError = (error: ApiError): void => {
  if (isBillingGateError(error)) {
    window.dispatchEvent(new Event(billingGateEvent));
  }
};
